import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getPickingLists,
  getPickingList,
  getPickingSummary,
  getMyPickingWorkload,
  generatePickingList,
  generatePickingListForWorkOrder,
  assignPicker,
  startPicking,
  completePicking,
  cancelPicking,
  confirmPick,
  reportShortage,
  skipPickItem,
} from '../api';
import type {
  PickingListFilters,
  GeneratePickingListInput,
  AssignPickerInput,
  ConfirmPickInput,
  ReportShortageInput,
  SkipItemInput,
} from '../types';
import { wmsWorkOrderKeys } from './useWmsWorkOrders';
import { wmsInventoryKeys } from './useWmsInventory';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsPickingKeys = {
  all: ['wms-picking'] as const,
  // No-arg call returns a prefix so invalidateQueries matches filtered lists.
  list: (filters?: PickingListFilters) => filters !== undefined
    ? [...wmsPickingKeys.all, 'list', filters] as const
    : [...wmsPickingKeys.all, 'list'] as const,
  detail: (id: string) => [...wmsPickingKeys.all, 'detail', id] as const,
  summary: () => [...wmsPickingKeys.all, 'summary'] as const,
  myWorkload: () => [...wmsPickingKeys.all, 'my-workload'] as const,
};

// ============================================================================
// Picking List Query Hooks
// ============================================================================

export function usePickingLists(filters: PickingListFilters = {}) {
  return useQuery({
    queryKey: wmsPickingKeys.list(filters),
    queryFn: () => getPickingLists(filters),
  });
}

export function usePickingList(id: string) {
  return useQuery({
    queryKey: wmsPickingKeys.detail(id),
    queryFn: () => getPickingList(id),
    enabled: !!id,
  });
}

export function usePickingSummary() {
  return useQuery({
    queryKey: wmsPickingKeys.summary(),
    queryFn: getPickingSummary,
  });
}

export function useMyPickingWorkload() {
  return useQuery({
    queryKey: wmsPickingKeys.myWorkload(),
    queryFn: getMyPickingWorkload,
  });
}

// ============================================================================
// Generate Picking List Hooks
// ============================================================================

export function useGeneratePickingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GeneratePickingListInput) => generatePickingList(data),
    onSuccess: (pickingList, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.summary() });
      // Invalidate work order as item statuses may change
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(variables.workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(variables.workOrderId) });
      toast.success(`Picking list "${pickingList.picking_number}" generated`);
    },
    onError: (error) => {
      toast.error('Failed to generate picking list', { description: getErrorMessage(error) });
    },
  });
}

export function useGeneratePickingListForWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workOrderId: string) => generatePickingListForWorkOrder(workOrderId),
    onSuccess: (pickingList, workOrderId) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.summary() });
      // Invalidate work order as item statuses may change
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      toast.success(`Picking list "${pickingList.picking_number}" generated`);
    },
    onError: (error) => {
      toast.error('Failed to generate picking list', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Picking Assignment Hooks
// ============================================================================

export function useAssignPicker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pickingListId, data }: { pickingListId: string; data: AssignPickerInput }) =>
      assignPicker(pickingListId, data),
    onSuccess: (_, { pickingListId }) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.myWorkload() });
      toast.success('Picker assigned successfully');
    },
    onError: (error) => {
      toast.error('Failed to assign picker', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Picking Status Transition Hooks
// ============================================================================

export function useStartPicking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pickingListId: string) => startPicking(pickingListId),
    onSuccess: (_, pickingListId) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.summary() });
      toast.success('Picking started');
    },
    onError: (error) => {
      toast.error('Failed to start picking', { description: getErrorMessage(error) });
    },
  });
}

export function useCompletePicking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pickingListId: string) => completePicking(pickingListId),
    onSuccess: (result, pickingListId) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.myWorkload() });
      // Invalidate work order as it may be updated
      if (result.workOrderId) {
        queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.workOrderId) });
        queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(result.workOrderId) });
      }
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Picking completed');
    },
    onError: (error) => {
      toast.error('Failed to complete picking', { description: getErrorMessage(error) });
    },
  });
}

export function useCancelPicking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pickingListId, reason }: { pickingListId: string; reason?: string }) =>
      cancelPicking(pickingListId, reason),
    onSuccess: (result, { pickingListId }) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.myWorkload() });
      // Invalidate work order as item statuses may revert
      if (result.workOrderId) {
        queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.workOrderId) });
        queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(result.workOrderId) });
      }
      toast.success('Picking cancelled');
    },
    onError: (error) => {
      toast.error('Failed to cancel picking', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Pick Item Action Hooks
// ============================================================================

export function useConfirmPick() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pickingListId, itemId, data }: { pickingListId: string; itemId: string; data: ConfirmPickInput }) =>
      confirmPick(pickingListId, itemId, data),
    onSuccess: (result, { pickingListId }) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      // Invalidate inventory as items have moved
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      // Invalidate work order items
      if (result.workOrderId) {
        queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(result.workOrderId) });
      }
      toast.success('Pick confirmed');
    },
    onError: (error) => {
      toast.error('Failed to confirm pick', { description: getErrorMessage(error) });
    },
  });
}

export function useReportShortage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pickingListId, itemId, data }: { pickingListId: string; itemId: string; data: ReportShortageInput }) =>
      reportShortage(pickingListId, itemId, data),
    onSuccess: (_, { pickingListId }) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.list() });
      toast.success('Shortage reported');
    },
    onError: (error) => {
      toast.error('Failed to report shortage', { description: getErrorMessage(error) });
    },
  });
}

export function useSkipPickItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pickingListId, itemId, data }: { pickingListId: string; itemId: string; data?: SkipItemInput }) =>
      skipPickItem(pickingListId, itemId, data),
    onSuccess: (_, { pickingListId }) => {
      queryClient.invalidateQueries({ queryKey: wmsPickingKeys.detail(pickingListId) });
      toast.success('Item skipped');
    },
    onError: (error) => {
      toast.error('Failed to skip item', { description: getErrorMessage(error) });
    },
  });
}
